/**
 * Card - Visualización de cuota mínima en mobile
 *
 * Respeta las columnas seleccionadas en el ColumnSelector.
 * Cliente (team_name) y estado (status) siempre visibles en el encabezado.
 */
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { Checkbox } from '@shared/components/ui/checkbox';
import { faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMinimumFeeInvoiceLabel,
  isMinimumFeeInvoiceable,
  MinimumFee,
  MinimumFeeStatus,
} from '../../models/MinimumFee';

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatMonthYear = (year: number | null | undefined, month: number | null | undefined): string => {
  if (!year || !month) return '-';
  return `${String(month).padStart(2, '0')}/${year}`;
};

const statusClassName: Record<MinimumFeeStatus, string> = {
  pending: 'bg-bg-warning text-warning',
  not_required: 'bg-muted text-muted-foreground',
  invoiced: 'bg-bg-success text-success',
};

export interface MinimumFeeCardProps {
  item: MinimumFee;
  className?: string;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  selectedColumns?: string[];
  onViewInvoice: (invoiceId: string) => void;
}

export const Card = React.memo(function Card({
  item: fee,
  className = '',
  isSelected,
  onToggleSelect,
  selectedColumns,
  onViewInvoice,
}: MinimumFeeCardProps) {
  const { t } = useTranslation('minimumFeesModule');
  const currency = useCurrency();
  const canSelect = isMinimumFeeInvoiceable(fee);

  const formatMoney = useMemo(
    () => (value: unknown) => {
      const amount = Number(value ?? 0);
      const formatted = new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number.isFinite(amount) ? amount : 0);

      return `${formatted} ${currency}`;
    },
    [currency],
  );

  const isColumnVisible = (columnId: string) => {
    if (!selectedColumns?.length) return true;
    return selectedColumns.includes(columnId);
  };

  const status = fee.status;

  return (
    <div className={`py-4 border-b border-card-border hover:bg-muted/30 transition-colors ${className}`}>
      {/* Encabezado - Cliente, estado, acciones */}
      <div className="flex items-center gap-2 mb-2 justify-between">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {canSelect && (
            <Checkbox
              className="w-4 h-4 shrink-0"
              checked={!!isSelected}
              onCheckedChange={onToggleSelect}
              data-testid="minimum-fee-card-checkbox"
            />
          )}
          <span className="font-medium text-base truncate">{fee.team_name || '-'}</span>
          {status && (
            <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${statusClassName[status]}`}>
              {t(`status.${status}`, status)}
            </span>
          )}
        </div>
        {fee.invoice_id && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 shrink-0"
            onClick={() => onViewInvoice(String(fee.invoice_id))}
            title={t('actions.viewInvoice', 'Ver factura')}
            aria-label={t('actions.viewInvoice', 'Ver factura')}
            data-testid="view-invoice-button"
          >
            <FontAwesomeIcon icon={faEye} className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {/* Detalle según columnas visibles */}
      <div className="flex flex-col gap-1">
        {isColumnVisible('period') && (
          <div className="flex items-center gap-2 w-full justify-between">
            <span className="text-sm text-complementary-text">{t('table.period', 'Año y mes')}:</span>
            <span className="text-sm font-medium">{formatMonthYear(fee.year, fee.month)}</span>
          </div>
        )}

        {isColumnVisible('minimum_fee') && (
          <div className="flex items-center gap-2 w-full justify-between">
            <span className="text-sm text-complementary-text">{t('table.minimumFee', 'Cuota mínima')}:</span>
            <span className="text-sm font-medium tabular-nums">{formatMoney(fee.minimum_fee)}</span>
          </div>
        )}

        {isColumnVisible('actual_billed_amount') && (
          <div className="flex items-center gap-2 w-full justify-between">
            <span className="text-sm text-complementary-text">
              {t('table.actualBilledAmount', 'Facturación real')}:
            </span>
            <span className="text-sm font-medium tabular-nums">{formatMoney(fee.actual_billed_amount)}</span>
          </div>
        )}

        {isColumnVisible('storage_amount') && (
          <div className="flex items-center gap-2 w-full justify-between">
            <span className="text-sm text-complementary-text">{t('table.storageAmount', 'Almacenaje')}:</span>
            <span className="text-sm font-medium tabular-nums">{formatMoney(fee.storage_amount)}</span>
          </div>
        )}

        {isColumnVisible('total_computed_amount') && (
          <div className="flex items-center gap-2 w-full justify-between">
            <span className="text-sm text-complementary-text">
              {t('table.totalComputedAmount', 'Total computado')}:
            </span>
            <span className="text-sm font-medium tabular-nums">{formatMoney(fee.total_computed_amount)}</span>
          </div>
        )}

        {isColumnVisible('difference_amount') && (
          <div className="flex items-center gap-2 w-full justify-between">
            <span className="text-sm text-complementary-text">
              {t('table.differenceAmount', 'Diferencia a facturar')}:
            </span>
            <span className="text-sm font-semibold tabular-nums">{formatMoney(fee.difference_amount)}</span>
          </div>
        )}

        {isColumnVisible('invoice_id') && (
          <div className="flex items-center gap-2 w-full justify-between">
            <span className="text-sm text-complementary-text">{t('table.invoice', 'Factura')}:</span>
            <span className="text-sm font-medium">{getMinimumFeeInvoiceLabel(fee)}</span>
          </div>
        )}

        {isColumnVisible('created_at') && (
          <div className="flex items-center gap-2 w-full justify-between">
            <span className="text-sm text-complementary-text">{t('table.createdAt', 'Fecha creación')}:</span>
            <span className="text-xs text-complementary-text">{formatDateTime(fee.created_at)}</span>
          </div>
        )}
      </div>
    </div>
  );
});
